import React, { useEffect, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";
import { OtcRequestItem, OtcSettlementProvider } from "../types";
import { convertToMinutes, VALID_FOR_UNITS } from "./ApproveRequestModal";
import { useOtcProviders } from "../hooks";
import { SettlementMethodDropdown } from "./SettlementMethodDropdown";

export interface SendOfferSubmitData {
  acceptRequestedRate: false;
  offeredRate: number;
  providerId: string;
  expiresInMinutes: number;
}

interface SendOfferModalProps {
  open: boolean;
  request: OtcRequestItem | null;
  onClose: () => void;
  onSubmit: (data: SendOfferSubmitData) => void;
  isSubmitting?: boolean;
}

export const SendOfferModal: React.FC<SendOfferModalProps> = ({
  open,
  request,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [offeredRate, setOfferedRate] = useState("");
  const [validForValue, setValidForValue] = useState<number | string>(7);
  const [validForUnit, setValidForUnit] = useState<string>("");
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");

  const currencyFrom = (request?.currency || "USD").toUpperCase();
  const currencyTo = (request?.currencyTo || "NGN").toUpperCase();

  const { providers } = useOtcProviders({
    currencyFrom,
    currencyTo,
    enabled: Boolean(open && request),
  });

  useEffect(() => {
    if (!open || !request) return;
    if (request.offeredRateValue) {
      setOfferedRate(request.offeredRateValue.toLocaleString());
    } else if (request.requestedRateValue) {
      setOfferedRate(request.requestedRateValue.toLocaleString());
    } else {
      setOfferedRate(
        request.requestedRate ? request.requestedRate.replace(/[^0-9.]/g, "") : ""
      );
    }

    setValidForValue(7);
    setValidForUnit("");

    if (providers.length > 0) {
      setSelectedProviderId((prev) => {
        const exists = providers.some((prov: OtcSettlementProvider) => prov.id === prev);
        return exists ? prev : providers[0].id;
      });
    }
  }, [open, request, providers]);

  if (!open || !request) return null;

  const selectedProvider =
    providers.find((prov: OtcSettlementProvider) => prov.id === selectedProviderId) || providers[0];
  const activeProviderId = selectedProvider?._id || selectedProviderId;

  const currentRateNum = parseFloat(offeredRate.replace(/,/g, "")) || 0;

  const isValidRate = currentRateNum > 0;
  const isValidForValue = Boolean(validForValue && Number(validForValue) > 0);
  const isValidForUnit = Boolean(validForUnit && validForUnit.trim());
  const isSettlementMethodSelected = Boolean(activeProviderId && activeProviderId.trim());
  const isFormValid =
    isValidRate &&
    isValidForValue &&
    isValidForUnit &&
    isSettlementMethodSelected;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const minutes = convertToMinutes(validForValue, validForUnit);

    onSubmit({
      acceptRequestedRate: false,
      offeredRate: currentRateNum,
      providerId: activeProviderId,
      expiresInMinutes: minutes,
    });
  };

  const requestedRateFormatted =
    request.requestedRate ||
    (request.requestedRateValue
      ? `₦${request.requestedRateValue.toLocaleString()}/USD`
      : "-");

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      overlayClassName="bg-black/60 backdrop-blur-sm"
      contentClassName="max-w-[490px] bg-white rounded-[20px] shadow-2xl border border-[#F2F4F7]"
    >
      <ModalHeader className="flex items-center justify-between px-6 pt-6 pb-2">
        <ModalTitle className="text-[20px] font-semibold text-[#101828]">
          Send offer
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          disabled={isSubmitting}
          onClick={onClose}
          className="rounded-full p-1 text-[#667085] hover:bg-[#F2F4F7] hover:text-[#101828] transition-colors disabled:opacity-50"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody className="flex flex-col gap-4 px-6 py-4">
          {/* Offered rate Field */}
          <div className="flex flex-col">
            <span className="text-[13px] font-medium text-[#344054] mb-1.5">
              Offered rate
            </span>
            <div className="flex h-[48px] w-full items-center justify-between rounded-[8px] bg-[#F2F4F7] px-3.5 focus-within:ring-1 focus-within:ring-[#D0D5DD]">
              <div className="flex items-center gap-1.5 w-full">
                <span className="text-[13px] font-medium text-[#101828]">₦</span>
                <input
                  type="text"
                  value={offeredRate}
                  onChange={(e) => setOfferedRate(e.target.value)}
                  className="w-full bg-transparent text-[13px] font-semibold text-[#101828] outline-none"
                  placeholder="1,450"
                />
              </div>
              <span className="text-[12px] font-normal text-[#475467] shrink-0 ml-2">
                NGN per USD
              </span>
            </div>
            <p className="text-[12px] text-[#475467] mt-1.5">
              Requested rate: {requestedRateFormatted}
            </p>
          </div>

          {/* Valid for row matching new design */}
          <div className="flex flex-col">
            <span className="text-[13px] font-medium text-[#344054] mb-1.5">
              Valid for
            </span>
            <div className="grid grid-cols-2 gap-3">
              {/* Number input */}
              <div className="flex h-[48px] w-full items-center rounded-[8px] bg-[#F2F4F7] px-3.5 focus-within:ring-1 focus-within:ring-[#D0D5DD]">
                <input
                  type="number"
                  min={1}
                  value={validForValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    setValidForValue(val === "" ? "" : Math.max(1, parseInt(val) || 1));
                  }}
                  placeholder="7"
                  className="w-full bg-transparent text-[14px] font-medium text-[#101828] outline-none"
                />
              </div>

              {/* Unit Dropdown */}
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    className="flex h-[48px] w-full items-center justify-between rounded-[8px] bg-[#F2F4F7] px-3.5 text-[14px] font-medium text-[#101828] outline-none hover:bg-[#EAECF0]"
                  >
                    <span className={validForUnit ? "text-[#101828]" : "text-[#667085]"}>
                      {validForUnit || "Minutes/Hours"}
                    </span>
                    <ChevronDown size={16} className="text-[#667085] shrink-0" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    align="start"
                    sideOffset={4}
                    className="z-[150] w-[var(--radix-dropdown-menu-trigger-width)] overflow-hidden rounded-[8px] border border-[#E4E7EC] bg-white p-1 shadow-md"
                  >
                    {VALID_FOR_UNITS.map((unit) => (
                      <DropdownMenu.Item
                        key={unit}
                        onSelect={() => setValidForUnit(unit)}
                        className={`flex items-center cursor-pointer rounded-[6px] px-3 py-2 text-[13px] outline-none transition-colors ${
                          validForUnit === unit
                            ? "bg-[#F4F5FA] font-medium text-[#101828]"
                            : "text-[#344054] hover:bg-[#F9FAFB]"
                        }`}
                      >
                        <span>{unit}</span>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
            <button
              type="button"
              onClick={() => {
                setValidForValue(30);
                setValidForUnit("Minutes");
              }}
              className="mt-1.5 text-left text-[12px] text-[#475467] transition-colors hover:text-[#101828]"
            >
              Recommended expiring: 30 minutes
            </button>
          </div>

          {/* Settlement Method */}
          <div className="flex flex-col">
            <span className="text-[13px] font-medium text-[#344054] mb-1.5">
              Settlement method
            </span>
            <SettlementMethodDropdown
              selectedProviderId={selectedProviderId}
              onSelectProvider={setSelectedProviderId}
              currencyFrom={currencyFrom}
              currencyTo={currencyTo}
              disabled={isSubmitting}
            />
            {requestedRateFormatted !== "-" && (
              <p className="text-[12px] text-[#475467] mt-1.5">
                Current rate: {requestedRateFormatted}
              </p>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="h-10 rounded-[8px] px-4 py-2 text-[13px] font-medium text-[#344054] transition-colors hover:text-[#101828] disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={isSubmitting || !isFormValid}
            >
              {isSubmitting ? "Sending..." : "Send offer"}
            </Button>
          </div>
        </ModalBody>
      </form>
    </Modal>
  );
};
